// =============================================================================
// BUDDY TRIPS ANALYTICS CARD — tenant-admin dashboard widget
//
// Calls the admin-buddy-analytics edge function (App DB) which wraps the
// get_buddy_tenant_analytics RPC. Renders a small KPI grid + headline
// line. Hidden entirely when no Buddy Trips activity has happened yet,
// to avoid "0 of 0" noise on tenants who haven't enabled it.
// =============================================================================

import { useEffect, useState } from 'react';
import { Users, MessageCircle, Send, Check } from 'lucide-react';

const APP_SUPABASE_URL = import.meta.env.VITE_APP_SUPABASE_URL || '';
const PROVISION_SECRET = import.meta.env.VITE_PROVISION_SECRET || '';

interface Analytics {
  total_opted_in: number;
  invitations_sent: number;
  invitations_accepted: number;
  invitations_declined: number;
  active_groups: number;
  total_messages: number;
  conversion_pct: number;
}

export function BuddyTripsAnalyticsCard({ tenantId }: { tenantId: string }) {
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!APP_SUPABASE_URL || !PROVISION_SECRET) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/admin-buddy-analytics`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-provision-secret': PROVISION_SECRET,
          },
          body: JSON.stringify({ mode: 'tenant', tenant_id: tenantId }),
        });
        const json = await res.json();
        if (!cancelled && json.ok) setData(json.data);
      } catch (err) {
        console.warn('[BuddyTripsAnalyticsCard] failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  if (loading || !data) return null;
  // Hide entirely when nothing's happened yet
  if (data.total_opted_in === 0 && data.invitations_sent === 0) return null;

  return (
    <div className="rounded-xl border border-surface-200 bg-surface-0 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Users size={16} className="text-emerald-600" />
        <h3 className="text-sm font-semibold text-surface-900">Buddy Trips</h3>
        {data.conversion_pct > 0 && (
          <span className="ml-auto text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-700/50 rounded-md px-2 py-0.5">
            {data.conversion_pct}% accept rate
          </span>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Stat icon={<Users size={14} />} label="Discoverable" value={data.total_opted_in} />
        <Stat icon={<Send size={14} />}  label="Invites sent" value={data.invitations_sent} />
        <Stat icon={<Check size={14} />} label="Accepted"     value={data.invitations_accepted} accent />
        <Stat icon={<MessageCircle size={14} />} label="Active groups" value={data.active_groups} />
      </div>

      {data.total_messages > 0 && (
        <p className="text-xs text-surface-500 mt-3">
          {data.total_messages.toLocaleString()} messages exchanged across active groups.
        </p>
      )}
    </div>
  );
}

function Stat({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: number; accent?: boolean }) {
  return (
    <div className={`p-3 rounded-lg border ${accent ? 'border-emerald-200/80 bg-emerald-50/40 dark:bg-emerald-900/10 dark:border-emerald-800/40' : 'border-surface-200 bg-surface-50/60 dark:border-surface-200 dark:bg-surface-0'}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-surface-500 mb-1">
        {icon} {label}
      </div>
      <div className="text-xl font-bold text-surface-900">{value.toLocaleString()}</div>
    </div>
  );
}
